import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'

const CHECKS = [
  'Refrigerator temperature logged daily and within 2–4°C',
  'All produce labelled with harvest date',
  'No expired stock in fridge or on shelf',
  'Harvest tools cleaned and stored correctly',
  'Packaging stock adequate (enough bags/clamshells)',
  'Rejects logged and disposed of correctly this week',
  'Traceability log up to date',
  'Gloves and hairnets available and in use',
  'Hand washing station stocked and functional',
  'No cross-contamination between Grade A and reject produce',
]

export default function QualityControl() {
  const { user, profile, canEditData } = useAuth()
  const [tab, setTab] = useState('audit')
  const [audits, setAudits] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [checks, setChecks] = useState(CHECKS.map(c => ({ label: c, result: '', notes: '' })))
  const [auditDate, setAuditDate] = useState(ugandaDate())
  const [auditor, setAuditor] = useState(profile?.full_name || '')
  const [overallNotes, setOverallNotes] = useState('')

  useEffect(() => { if (tab === 'history') loadAudits() }, [tab])

  const loadAudits = async () => {
    setLoading(true)
    const { data } = await supabase.from('visitor_logs')
      .select('*').order('created_at', { ascending: false }).limit(1)
    // Using a generic query — quality audits stored in their own table
    setLoading(false)
    setAudits([])
  }

  const setCheck = (i, field, val) => {
    const c = [...checks]
    c[i] = { ...c[i], [field]: val }
    setChecks(c)
  }

  const passed = checks.filter(c => c.result === 'Pass').length
  const failed = checks.filter(c => c.result === 'Fail').length
  const score = checks.filter(c => c.result).length > 0
    ? Math.round((passed / checks.filter(c => c.result).length) * 100) : 0

  const handleSave = async () => {
    setSaving(true)
    // Save as a JSON blob to incident_reports table temporarily
    const { error } = await supabase.from('incident_reports').insert({
      user_id: user.id,
      incident_date: new Date().toISOString(),
      incident_type: 'Quality Audit',
      description: JSON.stringify({ checks, score, passed, failed }),
      action_taken: overallNotes,
    })
    if (!error) { setSaved(true); setTimeout(() => setSaved(false), 3000) }
    setSaving(false)
  }

  return (
    <AppLayout title="Quality Control">
      <div className="section-header">
        <div class="section-icon">✅</div>
        <div className="section-info">
          <h2>Quality Control</h2>
          <p>Weekly food safety audit. Complete every Monday. Required for food safety compliance.</p>
        </div>
      </div>
      <div className="alert alert-amber">⚠️ Food safety is a legal requirement. Keep all audit records for minimum 2 years.</div>

      <div className="tabs">
        <button className={`tab ${tab==='audit'?'active':''}`} onClick={() => setTab('audit')}>📋 Weekly Audit</button>
        <button className={`tab ${tab==='grading'?'active':''}`} onClick={() => setTab('grading')}>🌾 Grading Standards</button>
        <button className={`tab ${tab==='rules'?'active':''}`} onClick={() => setTab('rules')}>📜 Food Safety Rules</button>
      </div>

      {tab === 'audit' && (
        <div className="card">
          {saved && <div className="alert alert-green">✅ Audit saved successfully!</div>}
          <div className="grid-2" style={{ marginBottom: 16 }}>
            <div className="form-group"><label className="form-label">Audit Date</label><input className="form-input" type="date" value={auditDate} onChange={e => setAuditDate(e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Auditor</label><input className="form-input" value={auditor} onChange={e => setAuditor(e.target.value)} /></div>
          </div>

          {/* Score summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, background: score >= 80 ? 'var(--green-pale)' : score >= 60 ? 'var(--amber-pale)' : 'var(--red-pale)', borderRadius: 10, padding: 16, marginBottom: 18 }}>
            {[['Score', score+'%', score>=80?'var(--green-mid)':score>=60?'var(--amber)':'var(--red)'],['Passed', passed, 'var(--green-mid)'],['Failed', failed, 'var(--red)']].map(([l,v,c]) => (
              <div key={l} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '0.65rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 4 }}>{l}</div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.6rem', fontWeight: 700, color: c }}>{v}</div>
              </div>
            ))}
          </div>

          <div className="table-wrap" style={{ marginBottom: 14 }}>
            <table>
              <thead><tr><th>Check</th><th style={{ width: 120 }}>Result</th><th>Notes</th></tr></thead>
              <tbody>
                {checks.map((c, i) => (
                  <tr key={i} style={{ background: c.result === 'Fail' ? '#fff8f8' : c.result === 'Pass' ? '#f8fff8' : undefined }}>
                    <td style={{ fontSize: '0.82rem' }}>{c.label}</td>
                    <td>
                      <select className="form-select" style={{ width: 110 }} value={c.result} onChange={e => setCheck(i, 'result', e.target.value)} disabled={!canEditData}>
                        <option value="">—</option>
                        <option>Pass</option>
                        <option>Fail</option>
                        <option>N/A</option>
                      </select>
                    </td>
                    <td><input className="form-input" placeholder="Notes..." value={c.notes} onChange={e => setCheck(i, 'notes', e.target.value)} disabled={!canEditData} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="form-group"><label className="form-label">Overall Notes / Actions Required</label><textarea className="form-textarea" value={overallNotes} onChange={e => setOverallNotes(e.target.value)} placeholder="Any overall observations or corrective actions needed..." disabled={!canEditData} /></div>
          {canEditData && <button className="btn btn-primary btn-lg" onClick={handleSave} disabled={saving}>{saving ? <><span className="spinner" />Saving...</> : '💾 Save Audit'}</button>}
        </div>
      )}

      {tab === 'grading' && (
        <div className="card">
          <div className="card-title">Grading Standards</div>
          <div className="card-subtitle">Apply these standards to every harvest. No exceptions.</div>
          <div className="table-wrap">
            <table>
              <thead><tr><th>Grade</th><th>Lettuce Standard</th><th>Strawberry Standard</th><th>Price / Action</th></tr></thead>
              <tbody>
                <tr><td><span className="badge badge-green">Grade A</span></td><td>150g+, full head, no damage, vibrant green, no tip burn</td><td>25g+, 75%+ red, no mould, calyx attached, firm</td><td>Full selling price</td></tr>
                <tr><td><span className="badge badge-amber">Grade B</span></td><td>100–150g or minor blemish, still fresh and safe</td><td>Small or minor surface mark, fresh, safe to eat</td><td>Discounted price or sampling</td></tr>
                <tr><td><span className="badge badge-red">Reject</span></td><td>Disease, mould, rot, insect damage, tip burn severe</td><td>Mould, rot, crushed, over-ripe, grey mould</td><td>Do NOT sell. Dispose safely. Log it.</td></tr>
              </tbody>
            </table>
          </div>
          <div className="alert alert-red" style={{ marginTop: 16 }}>🚨 If a customer reports illness — stop all sales immediately and call your local food safety authority. Do not wait.</div>
        </div>
      )}

      {tab === 'rules' && (
        <div className="card">
          <div className="card-title">Food Safety Rules — Non-Negotiable</div>
          <div className="card-subtitle">Every person on the farm must follow these rules at all times.</div>
          {[
            ['Before handling produce', ['Wash hands for 20 seconds with soap', 'Put on food-grade gloves', 'Put on hair covering', 'Check for open cuts or wounds — cover with blue plaster']],
            ['During harvesting and packing', ['Use only clean, sanitised tools', 'Never place produce directly on the floor', 'Keep Grade A separate from Grade B and rejects at all times', 'Do not eat, drink, or smoke in the growing or packing area']],
            ['After harvesting', ['Refrigerate produce within 30 minutes of harvest', 'Label every pack with harvest date and weight', 'Use FIFO — First In First Out. Oldest stock goes out first', 'Do not sell lettuce older than 5 days, strawberries older than 3 days']],
            ['If you feel unwell', ['Do NOT enter the farm if you have vomiting, diarrhoea, or a skin infection', 'Tell Sharon or Edward immediately', 'You will be sent home — this is not a punishment, it is food safety law']],
          ].map(([section, rules]) => (
            <div key={section} style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--green-deep)', marginBottom: 8, borderBottom: '1px solid var(--border)', paddingBottom: 4 }}>{section}</div>
              {rules.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, padding: '6px 0', fontSize: '0.83rem', borderBottom: '1px solid #f5f5f0' }}>
                  <span style={{ color: 'var(--green-bright)', fontWeight: 700 }}>✓</span>{r}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </AppLayout>
  )
}
